import React from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import type {
  GridActionsColDef,
  GridColDef,
  GridColumnVisibilityModel,
  GridRowParams,
  GridSortModel,
} from "@mui/x-data-grid";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { formatDateAsLocaleString } from "utils/formatters/DateFormatters";
import { nameof } from "utils/nameof";
import type { UserDto } from "client/api/UsersApi";
import { DataTable } from "components/DataTable";
import { UserRoutesMap } from "areas/users/UserRoutesMap";

interface UsersListTableProps {
  users: UserDto[];
  totalCount: number;
  loading?: boolean;
  page: number;
  pageSize?: number;
  initialSortModel: GridSortModel;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  onSortModelChange?: (mode: GridSortModel) => void;
  columnVisibilityModel?: GridColumnVisibilityModel;
  onColumnVisibilityModelChange?: (visModel: GridColumnVisibilityModel) => void;
}

const columns: GridColDef<UserDto>[] = [
  { field: nameof<UserDto>("email"), headerName: "Email", minWidth: 100, flex: 1 },
  { field: nameof<UserDto>("firstName"), headerName: "First Name", minWidth: 100, flex: 1 },
  { field: nameof<UserDto>("lastName"), headerName: "Last Name", minWidth: 100, flex: 1 },
  {
    field: nameof<UserDto>("createdDate"),
    headerName: "Created Date",
    minWidth: 100,
    flex: 1,
    valueFormatter: (value) => formatDateAsLocaleString(value),
  },
  { field: nameof<UserDto>("createdByUser"), headerName: "Created By", minWidth: 100, flex: 1 },
];

export function UserListTable(props: UsersListTableProps) {
  const {
    users,
    totalCount,
    loading,
    page,
    pageSize,
    onPageChange,
    onPageSizeChange,
    onSortModelChange,
    initialSortModel,
    columnVisibilityModel,
    onColumnVisibilityModelChange,
  } = props;
  const navigate = useNavigate();

  const actionCol: GridActionsColDef = {
    field: "actions",
    type: "actions",
    getActions: (params: GridRowParams) => [
      <Tooltip title="Show Detail" placement="right">
        <GridActionsCellItem
          icon={<Search />}
          onClick={() => navigate(UserRoutesMap.detail(params.id.toString()).root)}
          label="Open"
        />
      </Tooltip>,
    ],
  };

  return (
    <DataTable
      pagingMode={"server"}
      columns={[...columns, actionCol]}
      rows={users ?? []}
      rowCount={totalCount}
      initialState={{ sorting: { sortModel: initialSortModel } }}
      loading={loading}
      page={page}
      pageSize={pageSize}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      onSortModelChange={onSortModelChange}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={onColumnVisibilityModelChange}
    />
  );
}
